import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { History, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function CompetitionHistoryDialog({ competitionId, trigger, onDone }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/competitions/${competitionId}/history`);
      setEntries(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open, competitionId]);

  const rollback = async (e) => {
    if (!window.confirm(`Ripristinare "${e.changed_field}" al valore precedente "${e.old_value ?? "(vuoto)"}"?`)) return;
    try {
      await api.post(`/competitions/${competitionId}/rollback`, { field: e.changed_field, old_value: e.old_value });
      toast.success("Ripristinato");
      await load(); onDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div onClick={() => setOpen(true)}>{trigger || <Button size="sm" variant="outline"><History className="w-3.5 h-3.5 mr-1" />Storico</Button>}</div>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Storico modifiche</DialogTitle>
          <DialogDescription>Ogni modifica è non distruttiva. Puoi ripristinare un singolo campo al valore precedente.</DialogDescription>
        </DialogHeader>
        {loading && <div className="text-center py-6 text-zinc-500">Caricamento…</div>}
        {!loading && entries.length === 0 && <div className="text-center py-6 text-zinc-500">Nessuna modifica registrata.</div>}
        <div className="space-y-2">
          {entries.map((e) => (
            <div key={e.id} data-testid={`history-${e.id}`} className="bg-white border border-zinc-200 rounded-md p-3">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 text-xs">
                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-mono ${e.change_type === "rollback" ? "bg-purple-100 text-purple-800" : e.change_type === "created" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>{e.change_type}</span>
                  {e.changed_field && <span className="font-mono text-zinc-600">{e.changed_field}</span>}
                </div>
                <div className="text-[10px] text-zinc-500 font-mono">{e.created_at?.slice(0, 19).replace("T", " ")}</div>
              </div>
              {e.changed_field && (
                <div className="grid grid-cols-2 gap-3 text-sm mt-2">
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-mono">Precedente</div>
                    <div className="font-mono bg-red-50 border border-red-100 rounded p-2 text-red-800">{e.old_value ?? <span className="text-zinc-400">(vuoto)</span>}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-mono">Nuovo</div>
                    <div className="font-mono bg-emerald-50 border border-emerald-100 rounded p-2 text-emerald-800">{e.new_value ?? <span className="text-zinc-400">(vuoto)</span>}</div>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between mt-2 text-[10px] text-zinc-500 font-mono">
                <span>da {e.approved_by_name || "—"}{e.source_file_name ? ` · ${e.source_file_name}` : ""}</span>
                {e.change_type !== "created" && e.change_type !== "rollback" && (
                  <Button size="sm" variant="outline" onClick={() => rollback(e)} data-testid={`rollback-${e.id}`}>
                    <RotateCcw className="w-3 h-3 mr-1" />Ripristina
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
